import { DEFAULT_TIMEZONE } from 'constants/Misc';

import languageObj from './languageObj';

const countryFlags = {
  AR: '🇦🇷',
  BR: '🇧🇷',
};

export const DOCUMENT_TYPES_BY_COUNTRY = {
  AR: 'cuit-cuil',
  BR: 'cpf',
};

export function formatDocument(doc, type, countryIso = null) {
  const completeLength = 11;
  const cleanDoc = String(doc).replace(/[^\d]/g, '');
  let formattedDoc;
  if (cleanDoc.length === completeLength) {
    if (type === 'cpf') {
      formattedDoc = cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'cuit-cuil') {
      formattedDoc = cleanDoc.replace(/(\d{2})(\d{8})(\d{1})/, '$1-$2-$3');
    } else {
      formattedDoc = cleanDoc;
    }
  } else {
    formattedDoc = cleanDoc;
  }

  const flag = countryIso ? countryFlags[countryIso] : null;
  return flag ? `${flag} ${formattedDoc}` : formattedDoc;
}

const customCurrencySymbols = {
  USD: 'US$',
  BTC: 'BTC',
  USDT: 'USDT',
  ETH: 'ETH',
  TRX: 'TRX',
  LTC: 'LTC',
  BCH: 'BCH',
  USDC: 'USDC',
  XRP: 'XRP',
};

export const formatCurrency = (value, selectedCurrency, hideDecimals, decimalPlaces) => {
  const decimals = decimalPlaces ?? (hideDecimals ? 0 : 2);
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const prefix = customCurrencySymbols[selectedCurrency] ?? (selectedCurrency || 'ARS');

  const formattedNumber = new Intl.NumberFormat('es-AR', {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(absValue);

  return `${prefix} ${isNegative ? '-' : ''}${formattedNumber}`;
};

export const formatAmountWithoutCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

export const DECIMAL_INPUT_REGEX = /^\d{0,12}([.,]\d{0,2})?$/;

export const parseDecimalInput = (value) => Number(String(value).replace(',', '.'));

export const formatCurrencyWithoutSymbol = (value) => {
  const result = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
    currencyDisplay: 'code',
  }).format(value);

  return result.replace(/[a-z]{3}/i, '').trim();
};

// Formatea un valor numérico "crudo" (punto decimal, sin miles) al mostrarlo en un input editable,
// agregando separador de miles y usando coma decimal (es-AR), sin perder el valor interno en JS.
export const formatAmountDisplay = (val) => {
  if (val === null || val === undefined || val === '') return '';

  const stringVal = val.toString();
  const [integer, decimal] = stringVal.split('.');
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return decimal !== undefined ? `${formattedInteger},${decimal}` : formattedInteger;
};

// Inversa de formatAmountDisplay: toma lo que el usuario tipeó (con puntos de miles y coma decimal)
// y devuelve el valor "crudo" en formato JS (punto decimal, sin miles).
export const parseAmountRaw = (val) => {
  if (!val) return '';
  return val.replace(/\./g, '').replace(',', '.');
};

export const formatDate = (date, type = 'date', customTimezone) => {
  const language = JSON.parse(localStorage.getItem('locale'));
  const userTimeZone = JSON.parse(localStorage.getItem('client_timezone'));
  const timeZone = customTimezone ?? userTimeZone ?? DEFAULT_TIMEZONE;

  const options = {
    full: {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    },
    fullWithSeconds: {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone,
    },
    date: {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone,
    },
    dateNumeric: {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    },
    hour: {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    },
    hourWithSeconds: {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone,
    },
  };

  return new Date(date)
    .toLocaleString(languageObj[language?.locale] ?? 'pt-BR', options[type])
    .replace(/([ap])\.\sm\./i, '$1.m.')
    .replace(/ de /g, ' ')
    .replace('.', '');
};

export const parseDate = (date) => {
  if (!date) {
    return '/';
  }
  const d = date?.split('-');

  return `${d[2]}/${d[1]}/${d[0]}`;
};

export const formatNumericValue = (value) => {
  let result = value.replace(/[^0-9.]/g, '');

  if (result.split('.').length > 2) {
    result = result.replace(/\.+$/, '');
  }
  return result;
};

export const getMonthName = (monthNumber, type) => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(monthNumber - 1);
  const locale = JSON.parse(localStorage.getItem('locale'));
  const lang = `${locale?.locale ?? 'pt'}-${locale?.icon ?? 'br'}`;

  return date.toLocaleString(lang, { month: type ?? 'short' });
};

export const capitalizeText = (str = '') => {
  const words = str.toLowerCase().split(' ');
  return words
    .filter((str) => !!str.length)
    .map((word) => word[0].toUpperCase() + word.substring(1))
    .join(' ');
};
